import React from 'react';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import es from 'date-fns/locale/es';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formatDate, isValidDate, parseDate } from 'common/utils/dates';
import { getDateFormatByLocale } from 'common/utils/helpers/multiregion';

const ReactHookFormInputDatePicker = ({
    control,
    disableFuture,
    disablePast,
    disabled,
    format = getDateFormatByLocale(),
    fullWidth = true,
    inputFormat = 'dd/MM/yyyy',
    label,
    maxDate,
    minDate,
    name,
    onChange,
    placeholder,
    readOnly,
    sxInput = {},
    variant = 'outlined',
    views,
}) => {
    const { t } = useTranslation();
    const onChangeAction = ({ date, inputFormat, onChange, onChangeField }) => {
        if (isValidDate(date)) {
            const formatedDate = formatDate(date, inputFormat);
            onChangeField(formatedDate);
            if (onChange) onChange(formatedDate);
        }
    };

    return (
        <Controller
            control={control}
            name={name}
            render={({
                field: { onChange: onChangeField, value },
                fieldState: { error },
            }) => (
                <LocalizationProvider
                    adapterLocale={es}
                    dateAdapter={AdapterDateFns}
                >
                    <DatePicker
                        disableFuture={disableFuture}
                        disablePast={disablePast}
                        disabled={disabled}
                        format={format}
                        inputFormat={inputFormat}
                        label={label}
                        maxDate={maxDate}
                        minDate={minDate}
                        onChange={(date) =>
                            onChangeAction({
                                date,
                                inputFormat,
                                onChange,
                                onChangeField,
                            })
                        }
                        readOnly={readOnly}
                        slotProps={{
                            textField: {
                                error: Boolean(error),
                                fullWidth,
                                helperText: error?.message,
                                placeholder,
                                variant,
                                sx: sxInput,
                            },
                            toolbar: {
                                sx: {
                                    '& > .MuiPickersToolbar-content': { m: 0 },
                                },
                            },
                        }}
                        localeText={{
                            toolbarTitle: label,
                            cancelButtonLabel: t('Cancel'),
                        }}
                        value={value ? parseDate(value, inputFormat) : null}
                        views={views}
                    />
                </LocalizationProvider>
            )}
        />
    );
};

ReactHookFormInputDatePicker.propTypes = {
    control: PropTypes.object,
    disableFuture: PropTypes.bool,
    disablePast: PropTypes.bool,
    disabled: PropTypes.bool,
    format: PropTypes.string,
    fullWidth: PropTypes.bool,
    inputFormat: PropTypes.string,
    label: PropTypes.string,
    maxDate: PropTypes.instanceOf(Date),
    minDate: PropTypes.instanceOf(Date),
    name: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    readOnly: PropTypes.bool,
    sxInput: PropTypes.object,
    variant: PropTypes.string,
    views: PropTypes.array,
};

export default ReactHookFormInputDatePicker;
